import { Users } from "lucide-react";
import { ReactNode } from "react";
import { PageHeaderFrame } from "@/components/layout/page-header-frame";

interface TeamPageHeaderProps {
  isLeader: boolean;
  isLoading: boolean;
  /** Current report week, when known. Plain text — no BE endpoint yet for browsing past weeks. */
  weekNumber?: number;
  /** Team size / workload, woven into the description sentence (OKR-header pattern). */
  memberCount?: number;
  totalPriorities?: number;
  /** Attention pills (Need update / Missing Focus/Queue) — clickable filters only. */
  metrics?: ReactNode;
}

/** Inline stat inside the description sentence — bold value, muted sentence around it. */
const Stat = ({ value }: { value: number }) => (
  <span className="font-medium text-foreground">{value}</span>
);

/**
 * Page identity + health metrics, in one block: "what am I looking at" (title, Week N, counts in
 * the description sentence) then "what needs attention" (clickable pills). Plain counts live in
 * the sentence, not in pills — a pill implies an action, and these have none. Filters and Manage
 * Team live in a separate toolbar row rendered below this block by the page.
 */
export const TeamPageHeader = ({
  isLeader,
  isLoading,
  weekNumber,
  memberCount,
  totalPriorities,
  metrics,
}: TeamPageHeaderProps) => (
  <PageHeaderFrame
    icon={Users}
    title="My Team"
    description={
      isLoading ? undefined : isLeader && memberCount != null && totalPriorities != null ? (
        <>
          Managing <Stat value={memberCount} /> {memberCount === 1 ? "member" : "members"} with{" "}
          <Stat value={totalPriorities} /> active {totalPriorities === 1 ? "priority" : "priorities"}.
        </>
      ) : isLeader ? (
        "Manage your direct reports and oversee their active priorities."
      ) : (
        "Track your active priorities and progress."
      )
    }
    right={
      weekNumber != null || metrics ? (
        // Week + attention pills stack in the RIGHT column so the header never grows taller
        // than its title/description block just because pills appeared.
        <div className="flex flex-col items-end gap-2">
          {weekNumber != null && (
            <span className="text-lg font-semibold text-foreground">Week {weekNumber}</span>
          )}
          {!isLoading && metrics}
        </div>
      ) : undefined
    }
  />
);
